import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Dumbbell, Clock, ChevronDown, ChevronUp, Play, HeartPulse, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function BlessureModal({ schemaId, token, onClose, onDone }) {
  const [blessure, setBlessure] = useState("");
  const [busy, setBusy] = useState(false);
  const [resultaat, setResultaat] = useState(null);
  const [fout, setFout] = useState("");

  const verstuur = async () => {
    if (blessure.trim().length < 3 || busy) return;
    setBusy(true); setFout("");
    try {
      const r = await fetch(`${API}/api/pt/blessure`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ blessure: blessure.trim(), schema_id: schemaId }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Er ging iets mis");
      setResultaat(d.vervangingen || []);
      onDone();
    } catch (e) {
      setFout(e.message);
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative w-full max-w-md rounded-2xl p-6 surface-dark" style={{ background: "#111", border: "1px solid rgba(201,168,76,0.25)" }} onClick={e => e.stopPropagation()} data-testid="blessure-modal">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <HeartPulse size={16} className="text-[#C9A84C]" />
            <p className="text-white font-bold">Blessure melden</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="blessure-sluiten"><X size={18} /></button>
        </div>
        {resultaat === null ? (
          <>
            <p className="text-zinc-400 text-sm mb-3">Beschrijf je blessure. We passen je schema aan zodat je veilig kunt blijven trainen.</p>
            <textarea rows={3} value={blessure} onChange={e => setBlessure(e.target.value)}
              placeholder="bijv. pijn in mijn rechterschouder bij drukken boven het hoofd"
              data-testid="blessure-input"
              className="w-full bg-[#0d0d0d] border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50 placeholder-zinc-600" />
            {fout && <p className="text-red-400 text-xs mt-2">{fout}</p>}
            <button onClick={verstuur} disabled={busy || blessure.trim().length < 3} data-testid="blessure-versturen"
              className="w-full mt-4 py-3 rounded-xl font-bold text-sm text-black disabled:opacity-50" style={{ background: "#C9A84C" }}>
              {busy ? "Schema wordt aangepast..." : "Pas mijn schema aan"}
            </button>
          </>
        ) : (
          <div data-testid="blessure-resultaat">
            {resultaat.length === 0 ? (
              <p className="text-zinc-300 text-sm">Goed nieuws: je huidige schema belast deze blessure niet. Er hoefde niets aangepast te worden. Train bewust en luister naar je lichaam.</p>
            ) : (
              <>
                <p className="text-zinc-300 text-sm mb-3">We hebben {resultaat.length === 1 ? "1 oefening" : `${resultaat.length} oefeningen`} vervangen:</p>
                <div className="space-y-2 mb-2">
                  {resultaat.map((v, i) => (
                    <div key={i} className="rounded-xl border border-white/8 p-3 text-sm" style={{ background: "#0d0d0d" }}>
                      <p className="text-white font-semibold">{v.oude_naam} <span className="text-zinc-500">→</span> <span className="text-[#C9A84C]">{v.nieuwe_naam}</span></p>
                      {v.reden && <p className="text-zinc-500 text-xs mt-1">{v.reden}</p>}
                    </div>
                  ))}
                </div>
              </>
            )}
            <button onClick={onClose} className="w-full mt-3 py-3 rounded-xl font-bold text-sm text-black" style={{ background: "#C9A84C" }}>Sluiten</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function CustomSchemaViewer() {
  const { id: schemaId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [schema, setSchema] = useState(null);
  const [openDag, setOpenDag] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showBlessure, setShowBlessure] = useState(false);
  const [herlaad, setHerlaad] = useState(0);

  useEffect(() => {
    if (!user?.token) return;
    fetch(`${API}/api/my-custom-schemas/${schemaId}`, {
      headers: { Authorization: `Bearer ${user.token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setSchema(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [schemaId, user, herlaad]);

  if (loading) return (
    <div className="min-h-screen bg-[#080808] flex items-center justify-center">
      <div className="text-zinc-600 text-sm">Laden...</div>
    </div>
  );

  if (!schema) return (
    <div className="min-h-screen bg-[#080808] flex items-center justify-center">
      <div className="text-center">
        <p className="text-zinc-500 text-sm mb-4">Schema niet gevonden</p>
        <button onClick={() => navigate("/account")} className="btn-course-outline text-xs px-4 py-2">Terug</button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#080808] pb-16">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-[#080808]/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate("/account")} className="w-9 h-9 rounded-xl bg-[#141414] border border-white/6 flex items-center justify-center text-zinc-400 hover:text-white transition-colors">
            <ArrowLeft size={17} />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest truncate">{schema.doel} · {schema.niveau}</p>
            <h1 className="text-white font-bold text-base truncate">{schema.naam}</h1>
          </div>
          <button onClick={() => setShowBlessure(true)} data-testid="blessure-melden-btn" title="Blessure melden"
            className="w-9 h-9 rounded-xl bg-[#141414] border border-white/6 flex items-center justify-center text-[#C9A84C] hover:border-[#C9A84C]/40 transition-colors shrink-0">
            <HeartPulse size={16} />
          </button>
        </div>
      </div>

      {showBlessure && (
        <BlessureModal
          schemaId={schemaId}
          token={user?.token}
          onClose={() => setShowBlessure(false)}
          onDone={() => setHerlaad(h => h + 1)}
        />
      )}

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {/* Meta */}
        <div className="flex items-center gap-4 text-zinc-500 text-xs mb-6">
          <span className="flex items-center gap-1.5"><Dumbbell size={12} className="text-[#C9A84C]" /> {schema.trainingsdagen}x per week</span>
          <span className="flex items-center gap-1.5"><Clock size={12} className="text-[#C9A84C]" /> {schema.dagen?.reduce((a, d) => a + (d.oefeningen?.length || 0), 0)} oefeningen totaal</span>
        </div>

        {schema.beschrijving && (
          <div className="bg-[#0f0f0f] border border-white/6 rounded-2xl p-4">
            <p className="text-zinc-400 text-sm leading-relaxed">{schema.beschrijving}</p>
          </div>
        )}

        {/* Training days */}
        {schema.dagen?.map((dag, i) => (
          <div key={i} className="bg-[#0f0f0f] border border-white/6 rounded-2xl overflow-hidden">
            <button
              onClick={() => setOpenDag(openDag === i ? -1 : i)}
              className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/2 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-[#C9A84C]/15 flex items-center justify-center">
                  <span className="text-[#C9A84C] text-xs font-bold">{i + 1}</span>
                </div>
                <div className="text-left">
                  <p className="text-white font-bold text-sm">{dag.dag_naam}</p>
                  <p className="text-zinc-600 text-xs">{dag.oefeningen?.length || 0} oefeningen</p>
                </div>
              </div>
              {openDag === i ? <ChevronUp size={16} className="text-zinc-500" /> : <ChevronDown size={16} className="text-zinc-500" />}
            </button>

            {openDag === i && (
              <div className="border-t border-white/5">
                {/* Column headers */}
                <div className="grid grid-cols-4 px-5 py-2 border-b border-white/4 text-[10px] font-bold uppercase tracking-widest text-zinc-600">
                  <span className="col-span-2">Oefening</span>
                  <span className="text-center">Sets × Reps</span>
                  <span className="text-center">Rust</span>
                </div>

                {dag.oefeningen?.map((ex, j) => (
                  <div key={j} className="grid grid-cols-4 px-5 py-3 border-b border-white/4 last:border-0 hover:bg-white/2 transition-colors">
                    <div className="col-span-2">
                      <p className="text-white text-sm font-semibold">{ex.naam}</p>
                      {ex.spiergroep && <p className="text-zinc-600 text-xs">{ex.spiergroep}</p>}
                      {ex.tempo && <p className="text-zinc-600 text-xs mt-0.5">Tempo: {ex.tempo}</p>}
                      {ex.notities && <p className="text-[#C9A84C]/70 text-xs mt-0.5 italic">{ex.notities}</p>}
                    </div>
                    <div className="text-center self-center">
                      <span className="text-white font-bold text-sm">{ex.sets} × {ex.reps}</span>
                      {ex.gewicht && <p className="text-zinc-500 text-xs">{ex.gewicht}</p>}
                    </div>
                    <div className="text-center self-center text-[#C9A84C] text-sm font-semibold">{ex.rust}</div>
                  </div>
                ))}

                {/* Workout Mode button */}
                <div className="px-5 py-3">
                  <button
                    onClick={() => navigate(`/workout-custom/${schemaId}/${i}`)}
                    data-testid={`start-workout-dag-${i}`}
                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-blue-600/15 hover:bg-blue-600/25 text-blue-400 font-bold text-sm rounded-xl border border-blue-500/20 transition-colors"
                  >
                    <Play size={13} fill="currentColor" /> Training starten
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
